import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const Frame160: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState('certificate');
  const authenticationData = {
    itemId: 'AUTH-2024-VPW-001789',
    itemName: 'Victorian Pocket Watch',
    manufacturer: 'Elgin National Watch Company',
    period: '1892',
    authenticatedDate: 'December 10, 2024',
    expert: 'Dr. Margaret Chen',
    confidenceScore: 99.8,
    status: 'AUTHENTICATED',
    certificateNumber: 'EOE-CERT-2024-1789'
  };

  const analysisResults = [
    {
      category: 'Material Analysis',
      result: 'AUTHENTIC',
      confidence: 99.9,
      details: '14k Gold composition confirmed via X-ray fluorescence spectroscopy',
      icon: '🔬'
    },
    {
      category: 'Craftsmanship Verification',
      result: 'AUTHENTIC',
      confidence: 99.8,
      details: 'Hand-engraved patterns consistent with 1890s Elgin manufacturing techniques',
      icon: '🔨'
    },
    {
      category: 'Movement Authentication',
      result: 'AUTHENTIC',
      confidence: 99.7,
      details: 'Swiss mechanical movement serial number E-1890-4521 verified in manufacturer records',
      icon: '⚙️'
    },
    {
      category: 'Historical Verification',
      result: 'AUTHENTIC',
      confidence: 99.6,
      details: 'Production records confirm manufacture date of 1892, consistent with style and materials',
      icon: '📜'
    },
    {
      category: 'Provenance Check',
      result: 'VERIFIED',
      confidence: 98.9,
      details: 'Ownership history traced to William Heritage estate, documented for 130+ years',
      icon: '🏛️'
    },
    {
      category: 'Condition Assessment',
      result: 'EXCELLENT',
      confidence: 99.5,
      details: 'Minimal wear consistent with age, all original components intact and functional',
      icon: '✨'
    }
  ];

  const comparisonItems = [
    {
      id: 1,
      name: 'Similar Elgin Pocket Watch (1891)',
      result: 'Authentic',
      confidence: 98.2,
      notes: 'Comparable craftsmanship and materials',
      image: '/api/placeholder/80/80'
    },
    {
      id: 2,
      name: 'Counterfeit Elgin Watch (Modern)',
      result: 'Fake',
      confidence: 99.9,
      notes: 'Modern materials, incorrect serial pattern',
      image: '/api/placeholder/80/80'
    },
    {
      id: 3,
      name: 'Restored Elgin Watch (1890)',
      result: 'Authentic (Restored)',
      confidence: 95.4,
      notes: 'Original but with replacement components',
      image: '/api/placeholder/80/80'
    }
  ];

  const expertProfile = {
    name: 'Dr. Margaret Chen',
    title: 'Senior Heritage Authenticator',
    credentials: ['PhD Art History', 'IAA Certified', '25+ Years Experience'],
    specialties: ['Victorian Timepieces', 'American Watchmaking', 'Precious Metals'],
    certificationNumber: 'IAA-2024-MC-7891',
    signature: 'Dr. Margaret Chen, PhD'
  };

  const certificateData = {
    issueDate: 'December 10, 2024',
    validUntil: 'December 10, 2029',
    verificationCode: 'EOE-VERIFY-1789-2024',
    blockchainHash: '0x7f9a2b8c3d4e5f6a7b8c9d0e1f2a3b4c5d6e7f8a9b0c1d2e3f4a5b6c7d8e9f0a',
    qrCode: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCI+PHJlY3Qgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiIGZpbGw9IiNmZmYiLz48L3N2Zz4='
  };

  const tabs = [
    { id: 'certificate', name: 'Authentication Certificate', icon: '📜' },
    { id: 'analysis', name: 'Detailed Analysis', icon: '🔍' },
    { id: 'comparison', name: 'Comparative Analysis', icon: '⚖️' },
    { id: 'expert', name: 'Expert Profile', icon: '👨‍🎓' }
  ];

  return (
    <div className="min-h-screen bg-grey-50">
      {/* Header */}
      <header className="bg-white border-b border-grey-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/frame-159" className="text-blue-600 hover:text-blue-700 mr-4">
                ← Back to Expert Services
              </Link>
              <h1 className="text-xl font-display font-semibold text-grey-900">Authentication Results</h1>
            </div>
            <div className="flex items-center space-x-4">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                authenticationData.status === 'AUTHENTICATED' 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-red-100 text-red-800'
              }`}>
                ✓ {authenticationData.status}
              </span>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Authentication Summary */}
        <div className="bg-white rounded-lg border border-grey-200 p-8 mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Item Image */}
            <div className="lg:col-span-1">
              <img 
                src="/api/placeholder/400/400" 
                alt={authenticationData.itemName}
                className="w-full h-80 object-cover rounded-lg border border-grey-200"
              />
            </div>

            {/* Authentication Details */}
            <div className="lg:col-span-2">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-display font-bold text-grey-900 mb-2">
                    {authenticationData.itemName}
                  </h2>
                  <p className="text-lg text-grey-600 mb-1">{authenticationData.manufacturer}</p>
                  <p className="text-grey-500">Period: {authenticationData.period}</p>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-green-600 mb-1">
                    {authenticationData.confidenceScore}%
                  </div>
                  <p className="text-sm text-grey-600">Confidence Score</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6 mb-6">
                <div>
                  <p className="text-sm font-medium text-grey-900 mb-1">Authentication ID</p>
                  <p className="text-sm text-grey-600 font-mono">{authenticationData.itemId}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-grey-900 mb-1">Certificate Number</p>
                  <p className="text-sm text-grey-600 font-mono">{authenticationData.certificateNumber}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-grey-900 mb-1">Authenticated By</p>
                  <p className="text-sm text-grey-600">{authenticationData.expert}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-grey-900 mb-1">Date Completed</p>
                  <p className="text-sm text-grey-600">{authenticationData.authenticatedDate}</p>
                </div>
              </div>

              <div className="flex space-x-4">
                <button className="bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors">
                  Download Certificate
                </button>
                <button className="bg-grey-100 text-grey-700 px-6 py-2 rounded-lg text-sm font-medium hover:bg-grey-200 transition-colors">
                  Share Results
                </button>
                <button className="bg-grey-100 text-grey-700 px-6 py-2 rounded-lg text-sm font-medium hover:bg-grey-200 transition-colors">
                  Print Report
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-lg border border-grey-200 overflow-hidden">
          <div className="border-b border-grey-200">
            <nav className="flex space-x-8 px-6">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setSelectedTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    selectedTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-grey-500 hover:text-grey-700 hover:border-grey-300'
                  }`}
                >
                  <span className="mr-2">{tab.icon}</span>
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {/* Certificate Tab */}
            {selectedTab === 'certificate' && (
              <div className="space-y-6">
                <div className="text-center border-2 border-dashed border-grey-300 rounded-lg p-8">
                  <div className="max-w-2xl mx-auto">
                    <h3 className="text-2xl font-display font-bold text-grey-900 mb-4">
                      Official Authentication Certificate
                    </h3>
                    <div className="bg-gradient-to-br from-blue-50 to-green-50 border-2 border-blue-200 rounded-lg p-8 mb-6">
                      <div className="text-center mb-6">
                        <h4 className="text-xl font-bold text-grey-900 mb-2">End of an Era Heritage Authentication</h4>
                        <p className="text-grey-600">Certificate of Authenticity</p>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-6 text-left mb-6">
                        <div>
                          <p className="text-sm font-medium text-grey-900">Item:</p>
                          <p className="text-grey-700">{authenticationData.itemName}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-grey-900">Manufacturer:</p>
                          <p className="text-grey-700">{authenticationData.manufacturer}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-grey-900">Period:</p>
                          <p className="text-grey-700">{authenticationData.period}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-grey-900">Confidence:</p>
                          <p className="text-green-600 font-bold">{authenticationData.confidenceScore}%</p>
                        </div>
                      </div>
                      
                      <div className="border-t border-grey-200 pt-4">
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="text-sm text-grey-600">Authenticated by:</p>
                            <p className="font-medium text-grey-900">{expertProfile.signature}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-grey-600">Certificate #:</p>
                            <p className="font-mono text-sm text-grey-900">{authenticationData.certificateNumber}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-6 text-sm">
                      <div>
                        <p className="font-medium text-grey-900 mb-1">Issue Date:</p>
                        <p className="text-grey-600">{certificateData.issueDate}</p>
                      </div>
                      <div>
                        <p className="font-medium text-grey-900 mb-1">Valid Until:</p>
                        <p className="text-grey-600">{certificateData.validUntil}</p>
                      </div>
                      <div>
                        <p className="font-medium text-grey-900 mb-1">Verification Code:</p>
                        <p className="text-grey-600 font-mono text-xs">{certificateData.verificationCode}</p>
                      </div>
                      <div>
                        <p className="font-medium text-grey-900 mb-1">Blockchain Hash:</p>
                        <p className="text-grey-600 font-mono text-xs">{certificateData.blockchainHash.substring(0, 20)}...</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Analysis Tab */}
            {selectedTab === 'analysis' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {analysisResults.map((analysis, index) => (
                    <div key={index} className="bg-grey-50 rounded-lg p-6">
                      <div className="flex items-start space-x-4">
                        <span className="text-2xl">{analysis.icon}</span>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-medium text-grey-900">{analysis.category}</h4>
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              analysis.result === 'AUTHENTIC' || analysis.result === 'VERIFIED' || analysis.result === 'EXCELLENT'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {analysis.result}
                            </span>
                          </div>
                          <div className="mb-3">
                            <div className="flex items-center justify-between text-sm mb-1">
                              <span className="text-grey-600">Confidence</span>
                              <span className="font-medium text-grey-900">{analysis.confidence}%</span>
                            </div>
                            <div className="w-full bg-grey-200 rounded-full h-2">
                              <div 
                                className="bg-green-500 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${analysis.confidence}%` }}
                              ></div>
                            </div>
                          </div>
                          <p className="text-sm text-grey-600">{analysis.details}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Comparison Tab */}
            {selectedTab === 'comparison' && (
              <div className="space-y-6">
                <h3 className="text-lg font-display font-semibold text-grey-900">Comparative Analysis</h3>
                <div className="space-y-4">
                  {comparisonItems.map((item) => (
                    <div key={item.id} className="bg-grey-50 rounded-lg p-6">
                      <div className="flex items-center space-x-4">
                        <img src={item.image} alt={item.name} className="w-20 h-20 rounded-lg object-cover" />
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-medium text-grey-900">{item.name}</h4>
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                              item.result.includes('Authentic') 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {item.result}
                            </span>
                          </div>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm text-grey-600">Confidence: {item.confidence}%</span>
                            <div className="w-32 bg-grey-200 rounded-full h-2">
                              <div 
                                className={`h-2 rounded-full ${
                                  item.result.includes('Authentic') ? 'bg-green-500' : 'bg-red-500'
                                }`}
                                style={{ width: `${item.confidence}%` }}
                              ></div>
                            </div>
                          </div>
                          <p className="text-sm text-grey-600">{item.notes}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Expert Tab */}
            {selectedTab === 'expert' && (
              <div className="space-y-6">
                <div className="bg-grey-50 rounded-lg p-6">
                  <div className="flex items-start space-x-6">
                    <img src="/api/placeholder/120/120" alt={expertProfile.name} className="w-24 h-24 rounded-full" />
                    <div className="flex-1">
                      <h3 className="text-xl font-display font-semibold text-grey-900 mb-2">{expertProfile.name}</h3>
                      <p className="text-blue-600 font-medium mb-4">{expertProfile.title}</p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <h4 className="font-medium text-grey-900 mb-2">Credentials</h4>
                          <ul className="space-y-1">
                            {expertProfile.credentials.map((credential, index) => (
                              <li key={index} className="text-sm text-grey-600 flex items-center">
                                <span className="text-green-500 mr-2">•</span>
                                {credential}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <h4 className="font-medium text-grey-900 mb-2">Specialties</h4>
                          <div className="flex flex-wrap gap-2">
                            {expertProfile.specialties.map((specialty, index) => (
                              <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                                {specialty}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                      
                      <div className="mt-4 pt-4 border-t border-grey-200">
                        <p className="text-sm text-grey-600">
                          Certification Number: <span className="font-mono">{expertProfile.certificationNumber}</span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Frame160; 